import { VALHALLA_OPTIMIZED_ROUTE_ENDPOINT } from "../../clients/valhallaClient";
import type {
  Valhalla_CostingModel,
  Valhalla_Location,
  Valhalla_RouteError,
  Valhalla_RouteRes,
  ValhallaRouteResult,
} from "../../types/valhallaTypes";

/**
 * Fetches an optimized route plan from the Valhalla routing service.
 *
 * Sends a request to the Valhalla routing API with the provided locations and costing model.
 * If the response is successful, a `ValhallaRouteSuccess` object is returned.
 * If the response is a error, a `ValhallaRouteFailure` object is returned.
 *
 * @param locations The list of locations to fetch the route plan for.
 * @param costing The costing model to use. Defaults to "auto".
 * @returns A promise that resolves with a `ValhallaRouteResult` object.
 */
export async function fetchValhallaOptimizedRoutePlan(
  locations: Valhalla_Location[],
  costing: Valhalla_CostingModel = "auto",
): Promise<ValhallaRouteResult> {
  try {
    const json = {
      locations: locations,
      costing: costing,
    };

    const res = await fetch(
      `${VALHALLA_OPTIMIZED_ROUTE_ENDPOINT}?json=${JSON.stringify(json)}`,
    );

    if (!res.ok) {
      const convertedResponse: Valhalla_RouteError = await res.json();
      return {
        success: false,
        error: convertedResponse,
      };
    }

    const convertedResponse: Valhalla_RouteRes = await res.json();
    return {
      success: true,
      route: convertedResponse,
    };
  } catch (error) {
    return {
      success: false,
      error: {
        error_code: -1,
        error: error instanceof Error ? error.message : "Unknown error",
        status_code: 500,
        status: "Client Error",
      },
    };
  }
}
